import { useAppDispatch, useAppSelector } from '../../../../../../reduxStore/redux-hooks';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldErrors, FieldValues } from 'react-hook-form';
import { MAX_LENGTH_DATASET_NAME, MIN_LENGTH_DATASET_NAME } from '../../../../../staticComponents/StaticHtmlGenerator';
import { useEffect } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { updateFileSelectionState } from '../../../../../../reduxStore/slices/FileUploadSlice';
import type { SelectChangeEvent } from '@mui/material/Select';

interface FormValidationProps<T extends FieldValues> {
  control: Control<T>;
  errors: FieldErrors<T>;
  reset: (values: Partial<T>) => void;
}

interface NewDatasetProps {
  formValidation: FormValidationProps<{
    datasetName: string;
    datasetDescription: string;
  }>;
}

const NewDataset: React.FC<NewDatasetProps> = ({ formValidation }: NewDatasetProps) => {
  const { t } = useTranslation();
  const { fileUpload, fileSelection } = useAppSelector((state) => state.fileUpload);
  const { isCustomAppSelected } = useAppSelector((state) => state.customApps.choosenCustomApp);
  const { control, errors, reset } = formValidation;
  const dispatch = useAppDispatch();

  const datasetType = fileSelection.complexDocumentsEnabled ? 'complex' : 'general';

  const handleDatasetTypeChange = (event: SelectChangeEvent<'complex' | 'general'>) => {
    const value = event.target.value as string;
    if (value === 'complex') {
      dispatch(
        updateFileSelectionState({
          complexDocumentsEnabled: true,
          isVisionSummaryEnabled: false,
        })
      );
    } else {
      dispatch(
        updateFileSelectionState({
          complexDocumentsEnabled: false,
          isVisionSummaryEnabled: false,
        })
      );
    }
  };

  useEffect(() => {
    if (isCustomAppSelected) {
      dispatch(
        updateFileSelectionState({
          complexDocumentsEnabled: true,
          isVisionSummaryEnabled: false,
        })
      );
    }
  }, [isCustomAppSelected, dispatch]);

  useEffect(() => {
    reset({
      datasetName: fileUpload.uploadedFiles.length > 0 ? fileUpload.uploadedFiles[0].name : '',
      datasetDescription: '',
    });
  }, [fileUpload.uploadedFiles, reset]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Controller
        control={control}
        name="datasetName"
        rules={{
          required: t('rightPanel.dataset.errorsValidationMessages.nameRequired'),
          validate: {
            noOnlySpaces: (value) => {
              const isValid = value.trim().length > MIN_LENGTH_DATASET_NAME;
              return isValid || t('rightPanel.dataset.errorsValidationMessages.nameRequired');
            },
            maxLength: (value) => {
              const isValid = value.trim().length <= MAX_LENGTH_DATASET_NAME;
              return isValid || t('rightPanel.dataset.errorsValidationMessages.nameCharacterLimit');
            },
          },
        }}
        render={({ field }) => {
          const truncatedDatasetName =
            field.value.length > MAX_LENGTH_DATASET_NAME
              ? `${field.value.slice(0, MAX_LENGTH_DATASET_NAME)}`
              : field.value;

          return (
            <FormControl fullWidth variant="standard" sx={{ marginBottom: '1.5rem' }}>
              <Typography fontSize={16} fontWeight={700} id="dataset-label" component="label" htmlFor="dataset-name">
                {t('fileUpload.enterName')} <span aria-hidden="true">*</span>
              </Typography>
              <Input
                {...field}
                fullWidth
                id="dataset-name"
                value={truncatedDatasetName}
                sx={{ padding: '0.2rem 0rem' }}
                error={!!errors.datasetName}
                inputProps={{
                  maxLength: MAX_LENGTH_DATASET_NAME + 1,
                  'aria-labelledby': 'dataset-label',
                  'aria-required': 'true',
                  'aria-label': `${t('fileUpload.enterName')} edit required blank`,
                }}
              />
              <Box>
                <FormHelperText
                  error
                  sx={{
                    position: 'absolute',
                    width: '100%',
                  }}
                >
                  {!!errors.datasetName && errors.datasetName.message}
                </FormHelperText>
              </Box>
            </FormControl>
          );
        }}
      ></Controller>

      <Controller
        control={control}
        name="datasetDescription"
        rules={{
          maxLength: { value: 50, message: t('rightPanel.dataset.errorsValidationMessages.descriptionCharacterLimit') },
        }}
        render={({ field }) => (
          <FormControl fullWidth variant="standard" sx={{ marginBottom: '1.5rem' }}>
            <Typography
              fontSize={16}
              fontWeight={700}
              id="dataset-description-label"
              component="label"
              htmlFor="dataset-description"
            >
              {t('fileUpload.description')}
            </Typography>
            <Input
              {...field}
              fullWidth
              id="dataset-description"
              sx={{ padding: '0.2rem 0rem' }}
              error={!!errors.datasetDescription}
              inputProps={{
                'aria-labelledby': 'dataset-description-label',
                'aria-label': `${t('fileUpload.description')} edit optional ${t(
                  'rightPanel.dataset.errorsValidationMessages.descriptionCharacterLimit'
                )} blank`,
              }}
            />
            <Box>
              <FormHelperText
                error
                sx={{
                  position: 'absolute',
                  width: '100%',
                }}
              >
                {!!errors.datasetDescription && errors.datasetDescription.message}
              </FormHelperText>
            </Box>
          </FormControl>
        )}
      ></Controller>
      {/* Hide dataset type selector for custom apps - they are always complex */}
      {!isCustomAppSelected && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="dataset-type-label">{'Dataset Type'}</InputLabel>
          <Select
            labelId="dataset-type-label"
            id="dataset-type"
            value={datasetType}
            label={'Dataset Type'}
            onChange={handleDatasetTypeChange}
          >
            <MenuItem value="general">{'General purpose dataset'}</MenuItem>
            <MenuItem value="complex">{'Dataset with complex tables, charts'}</MenuItem>
          </Select>
        </FormControl>
      )}
      {datasetType === 'general' && !isCustomAppSelected && (
        <FormControlLabel
          control={
            <Checkbox
              checked={fileSelection.isVisionSummaryEnabled}
              onChange={(e) =>
                dispatch(
                  updateFileSelectionState({
                    isVisionSummaryEnabled: e.target.checked,
                  })
                )
              }
            />
          }
          label={t('fileUpload.visionSummary')}
          sx={{ mb: 2 }}
        />
      )}
    </Box>
  );
};

export default NewDataset;
